'use client';

import { useState, useCallback } from 'react';
import { Button } from './button';
import { Label } from './label';
import { createClient } from '@/lib/supabase/client';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

export interface UploadedFile {
  name: string;
  url: string;
  type: string;
  size: number;
  /** S3 object key — present for files uploaded via the presign flow. */
  key?: string;
}

interface FileUploadProps {
  label?: string;
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  maxFiles?: number;
  subfolder: string; // Required: API-side subfolder identifier (e.g. "event/banner")
  className?: string;
  id?: string; // Optional unique ID for the file input
  accept?: string; // Optional: file types to accept (e.g., "image/*", "application/pdf")
  // Translation props
  dragDropText?: string;
  acceptedFilesText?: string;
  selectFilesText?: string;
  uploadingText?: string;
  uploadedFilesText?: string;
  deleteText?: string;
}

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
}

async function uploadOneFile(
  file: File,
  subfolder: string,
  authHeader: string,
): Promise<UploadedFile> {
  const presignRes = await fetch('/api/upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    body: JSON.stringify({
      subfolder,
      filename: file.name,
      contentType: file.type,
      contentLength: file.size,
    }),
  });

  if (!presignRes.ok) {
    let message = 'Failed to prepare upload';
    try {
      const data = await presignRes.json();
      message = data.error || message;
    } catch {
      // ignore
    }
    throw new Error(`${file.name}: ${message}`);
  }

  const { uploadUrl, publicUrl, key } = (await presignRes.json()) as PresignResponse;

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!putRes.ok) {
    throw new Error(`${file.name}: upload to storage failed (${putRes.status})`);
  }

  return {
    name: file.name,
    url: publicUrl,
    type: file.type,
    size: file.size,
    key,
  };
}

export function FileUpload({
  label = 'Upload Files',
  value = [],
  onChange,
  maxFiles = 10,
  subfolder,
  className = '',
  id,
  accept,
  // Translation props with defaults
  dragDropText = 'Drag and drop files here, or click to select',
  acceptedFilesText = 'Accepted: PDF, Images, Documents, Excel, Text files (max 50MB each)',
  selectFilesText = 'Select Files',
  uploadingText = 'Uploading...',
  uploadedFilesText = 'Uploaded Files',
  deleteText: _deleteText = 'Delete',
}: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <PhotoIcon className="h-4 w-4" />;
    if (type === 'application/pdf') return <DocumentTextIcon className="h-4 w-4" />;
    return <DocumentIcon className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = useCallback(
    async (files: FileList) => {
      // Snapshot FileList synchronously — it can be cleared during async work.
      const fileArray = Array.from(files);
      if (fileArray.length === 0) {
        setError('No files selected');
        return;
      }

      if (value.length >= maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      setUploading(true);
      setError(null);

      try {
        if (!subfolder) {
          throw new Error('Subfolder is required for file uploads');
        }

        const supabase = createClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const authHeader = session?.access_token ? `Bearer ${session.access_token}` : '';
        if (!authHeader) {
          throw new Error('Not authenticated');
        }

        // Upload up to 4 files at once. Each upload is a presign roundtrip
        // plus a direct PUT to S3 — they're fully independent, so parallelism
        // is just bounded by browser connection limits and our friendliness
        // budget. Failures are isolated: one bad file doesn't abort the rest.
        const CONCURRENCY = 4;
        const uploadedFiles: UploadedFile[] = new Array(fileArray.length);
        let cursor = 0;
        const errors: string[] = [];
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, fileArray.length) }, async () => {
            while (true) {
              const idx = cursor++;
              if (idx >= fileArray.length) return;
              try {
                uploadedFiles[idx] = await uploadOneFile(fileArray[idx], subfolder, authHeader);
              } catch (err) {
                errors.push(err instanceof Error ? err.message : 'upload failed');
              }
            }
          }),
        );
        const successful = uploadedFiles.filter((f): f is UploadedFile => Boolean(f));
        if (errors.length > 0 && successful.length === 0) {
          throw new Error(errors.join('; '));
        }
        if (errors.length > 0) {
          setError(errors.join('; '));
        }

        const newFiles = [...value, ...successful];
        onChange?.(newFiles);
      } catch (err) {
        console.error('Upload error:', err);
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [value, maxFiles, onChange, subfolder],
  );

  const handleFileDelete = useCallback(
    async (index: number) => {
      const fileToDelete = value[index];

      try {
        const supabase = createClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const authHeader = session?.access_token ? `Bearer ${session.access_token}` : '';
        if (!authHeader) {
          throw new Error('Not authenticated');
        }

        // Only attempt remote delete when we have an S3 key. Legacy files without
        // a key are removed from local state only — callers persist the URL list
        // and the orphaned object will be cleaned up by lifecycle/audit jobs.
        if (fileToDelete.key) {
          const res = await fetch('/api/upload', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify({ key: fileToDelete.key }),
          });
          if (!res.ok) {
            let message = 'Failed to delete file';
            try {
              const data = await res.json();
              message = data.error || message;
            } catch {
              // ignore
            }
            throw new Error(message);
          }
        }

        const newFiles = value.filter((_, i) => i !== index);
        onChange?.(newFiles);
      } catch (err) {
        console.error('Delete error:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete file');
      }
    },
    [value, onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer.files) {
        handleFileUpload(e.dataTransfer.files);
      }
    },
    [handleFileUpload],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleAreaClick = useCallback(() => {
    if (!uploading && value.length < maxFiles) {
      const fileInputId = id || 'file-upload';
      document.getElementById(fileInputId)?.click();
    }
  }, [uploading, value.length, maxFiles, id]);

  return (
    <div className={`space-y-4 ${className}`}>
      {label && <Label>{label}</Label>}

      {/* Upload Area */}
      <div
        className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleAreaClick}
      >
        <ArrowUpTrayIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
        <p className="mb-2 text-sm text-gray-600">{dragDropText}</p>
        <p className="mb-4 text-xs text-gray-500">{acceptedFilesText}</p>

        <input
          type="file"
          multiple
          accept={
            accept ||
            'application/pdf,image/jpeg,image/jpg,image/png,image/gif,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv'
          }
          onChange={(e) => {
            e.preventDefault();
            if (e.target.files && e.target.files.length > 0) {
              handleFileUpload(e.target.files);
              e.target.value = '';
            }
          }}
          className="hidden"
          id={id || 'file-upload'}
          disabled={uploading || value.length >= maxFiles}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading || value.length >= maxFiles}
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            const fileInputId = id || 'file-upload';
            document.getElementById(fileInputId)?.click();
          }}
        >
          {uploading ? uploadingText : selectFilesText}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* File List */}
      {value.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            {uploadedFilesText} ({value.length}/{maxFiles})
          </h4>
          <div className="space-y-2">
            {value.map((file, index) => (
              <div
                key={index}
                className="bg-surface flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center space-x-3">
                  {getFileIcon(file.type)}
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileDelete(index)}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
